import type { MCategories } from "@/Models/DepartamentCategory/DepartamentCategory";
import { useDepartamentCategorie } from "@/Service/Api/ApiDepartamentCategorie/DepartamentCategorie";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

type HeaderContextType = {
  cacheDepartamentoData: MCategories[];
  cacheIsLoadingDepartament: boolean;
  ViewDataDepartament: (values: number) => void;
};

const STORAGE_KEY = "departamentDataCache";
const CACHE_TTL = 60 * 60 * 1000; // 1 hora en milisegundos

type CacheItem<T> = {
  data: T;
  timestamp: number;
};

export const HeaderContext = createContext<HeaderContextType | undefined>(
  undefined
);

export const ContextHeaderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  //departament-category
  const { isLoading: isLoadingDepartament, departamentData } =
    useDepartamentCategorie();
  //use ref
  const cacheRef = useRef<MCategories[] | null>(null);

  const ViewDataDepartament = useCallback(
    (values: number) => {
      //revisar localStorage con expiración.
      const storedItem = localStorage.getItem(STORAGE_KEY);
      if (storedItem) {
        try {
          const parsed = JSON.parse(storedItem) as CacheItem<
            MCategories[] | null
          >;
          const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;

          if (!isExpired && parsed.data && parsed.data.length > 0) {
            cacheRef.current = parsed.data;
            return cacheRef.current;
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch (error) {
          console.log("Error al parsear : ", error);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      return cacheDepartamentoData;
    },
    [departamentData]
  );

  // Guardar en memoria los datos ordenados
  const cacheDepartamentoData = useMemo(() => {
    const sorted = [...departamentData].sort((a, b) =>
      a.Name.localeCompare(b.Name)
    );
    cacheRef.current = sorted;
    return sorted || [];
  }, [departamentData]);

  const cacheIsLoadingDepartament = useMemo(() => {
    return isLoadingDepartament;
  }, [isLoadingDepartament]);

  return (
    <HeaderContext.Provider
      value={{
        cacheDepartamentoData,
        cacheIsLoadingDepartament,
        ViewDataDepartament,
      }}>
      {children}
    </HeaderContext.Provider>
  );
};

export const useContextHeaderData = () => {
  const context = useContext(HeaderContext);
  if (!context)
    throw new Error(
      "useContextHomeData must be used within a ContextHeaderProvider"
    );

  return context;
};
